import { useId, useMemo, useState } from 'react';
import Icon from '@amzn/meridian/icon';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import chevronDownIconTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import { css } from '@emotion/css';
import useSurfaceStyles from '../hooks/useSurfaceStyles';
import { accentHoverColor } from '../theme/accent';
import { createTransition, motionDurations, motionEasing, reducedMotionQuery } from '../theme/motion';
import CopyableValue from './CopyableValue';

const FILTER_VISIBLE_FROM_ENDPOINT_COUNT = 8;

export default function EndpointsDisclosure({ endpoints }: { endpoints: string[] }) {
  const meridianTheme = useMeridianTheme();
  const isDarkMode = meridianTheme.mode === 'dark';
  const { accentTintColor, fieldClass } = useSurfaceStyles();
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [endpointFilter, setEndpointFilter] = useState('');
  const visibleEndpoints = useMemo(
    () => endpoints.filter((endpoint) => endpoint.toLowerCase().includes(endpointFilter.trim().toLowerCase())),
    [endpoints, endpointFilter],
  );
  const showFilter = endpoints.length >= FILTER_VISIBLE_FROM_ENDPOINT_COUNT;

  const containerClass = css({
    borderTop: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    paddingTop: 4,
  });
  const chevronClass = css({
    display: 'flex',
    color: meridianTheme('themeInkSubdued'),
    transform: isOpen ? 'rotate(180deg)' : 'none',
    transition: `transform ${motionDurations.moderate} ${motionEasing.standard}, color ${motionDurations.instant} ${motionEasing.standard}`,
    [reducedMotionQuery]: { transition: 'none' },
  });
  // The card around this clips horizontal overflow, so the toggle stays flush with the content and
  // signals hover through color rather than a background that would be cut off at the edges.
  const toggleClass = css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '10px 0',
    border: 0,
    borderRadius: 6,
    backgroundColor: 'transparent',
    color: meridianTheme('themeInkDefault'),
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
    transition: createTransition(['color'], motionDurations.instant),
    '&:hover': { color: meridianTheme('themeActionPrimaryDefault') },
    [`&:hover .${chevronClass}`]: { color: meridianTheme('themeActionPrimaryDefault') },
    '&:focus-visible': { outline: `2px solid ${meridianTheme('themeBorderFocus')}`, outlineOffset: 2 },
  });
  const countClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 7px',
    borderRadius: 999,
    backgroundColor: accentTintColor,
    color: meridianTheme('themeActionPrimaryDefault'),
    fontSize: 12,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  });
  const spacerClass = css({ flex: '1 1 auto' });
  // Animating grid rows from 0fr to 1fr transitions to the content's natural height without measuring it.
  const collapseClass = css({
    display: 'grid',
    gridTemplateRows: isOpen ? '1fr' : '0fr',
    visibility: isOpen ? 'visible' : 'hidden',
    transition: `grid-template-rows ${motionDurations.moderate} ${motionEasing.standard}, visibility ${motionDurations.moderate}`,
    [reducedMotionQuery]: { transition: 'none' },
  });
  const collapseInnerClass = css({ minHeight: 0, overflow: 'hidden' });
  const panelClass = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '4px 0 8px',
  });
  const filterClass = css(fieldClass, {
    width: '100%',
    height: 32,
    padding: '0 10px',
    borderRadius: 8,
    fontSize: 13,
  });
  const listClass = css({
    maxHeight: 280,
    margin: 0,
    padding: 4,
    overflowY: 'auto',
    listStyle: 'none',
    borderRadius: 10,
    border: `1px solid ${meridianTheme('themeBorderSubdued')}`,
  });
  const itemClass = css({
    padding: '6px 8px',
    borderRadius: 6,
    color: meridianTheme('themeInkDefault'),
    fontSize: 13,
    lineHeight: '20px',
    transition: createTransition(['background-color'], motionDurations.instant),
    '&:hover': { backgroundColor: accentHoverColor(isDarkMode) },
  });
  const emptyClass = css({
    padding: '8px',
    color: meridianTheme('themeInkSubdued'),
    fontSize: 13,
  });

  return (
    <div className={containerClass}>
      <button
        type="button"
        className={toggleClass}
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
      >
        Endpoints
        <span className={countClass}>{endpoints.length}</span>
        <span className={spacerClass} />
        <span className={chevronClass}>
          <Icon tokens={chevronDownIconTokens} />
        </span>
      </button>
      <div id={contentId} className={collapseClass}>
        <div className={collapseInnerClass}>
          <div className={panelClass}>
            {showFilter && (
              <input
                type="text"
                className={filterClass}
                value={endpointFilter}
                onChange={(event) => setEndpointFilter(event.target.value)}
                placeholder={`Filter ${endpoints.length} endpoints`}
                aria-label="Filter endpoints"
                autoComplete="off"
                spellCheck={false}
              />
            )}
            <ul className={listClass}>
              {visibleEndpoints.map((endpoint) => (
                <li key={endpoint} className={itemClass}>
                  <CopyableValue text={endpoint} />
                </li>
              ))}
              {visibleEndpoints.length === 0 && <li className={emptyClass}>No endpoints match the filter.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
